import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Car, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Seo } from "@/components/Seo";

export default function AdminLogin() {
  const { login, user, checked } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (checked && user) navigate("/admin", { replace: true });
  }, [checked, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center bg-slate-50 px-5">
      <Seo noindex title="Admin | Belo Rentals" path="/admin/login" />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2.5">
            <span className="grid place-items-center w-9 h-9 rounded-xl bg-blue-600 text-white"><Car size={18} /></span>
            <span className="font-heading text-xl font-bold tracking-tight text-slate-900">Belo<span className="text-blue-600">Rentals</span></span>
          </div>
          <p className="label-caps text-slate-400 mt-3">Admin Portal</p>
        </div>

        <form onSubmit={submit} data-testid="admin-login-form" className="bg-white rounded-2xl shadow-card p-8">
          <div className="flex items-center gap-3 text-slate-900 mb-7">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-blue-50 text-blue-600"><Lock size={18} /></span>
            <h1 className="font-heading text-xl font-semibold">Sign in</h1>
          </div>

          {error && (
            <div data-testid="login-error" className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-xl mb-6">
              {error}
            </div>
          )}

          <div className="space-y-5">
            <div>
              <span className="label-caps text-slate-400 block mb-2">Email</span>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="belo-input" placeholder="admin@belorentals.com" />
            </div>
            <div>
              <span className="label-caps text-slate-400 block mb-2">Password</span>
              <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="belo-input" placeholder="••••••••" />
            </div>
          </div>

          <button type="submit" disabled={loading} data-testid="login-submit" className="mt-7 w-full bg-blue-600 text-white py-3.5 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50">
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <Link to="/" data-testid="admin-back-home" className="mt-6 flex items-center justify-center gap-2 text-sm font-medium text-slate-500 hover:text-blue-600 transition-colors">
          <ArrowLeft size={15} /> Back to Home
        </Link>
      </div>
    </div>
  );
}
